import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Grid from '@material-ui/core/Grid';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core/styles';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import { useInput, FieldTitle } from 'react-admin';
import VirtualizedGrid from '../../components/VirtualizedGrid';
import LightTooltip from '../LightTooltip';
import StatusColorDisplay from '../StatusColorDisplay';
import { uniqBy } from 'lodash';
import { Autocomplete } from '@material-ui/lab';

const styles = () => ({
  checkboxCell: {
    width: '24px',
    borderBottom: '0px !important'
  },
  checkbox: {
    padding: '0'
  }
});

/**
 * An Input component for a select box, using an array of objects for the options
 */
const GridInput = props => {
  const {
    format,
    onBlur,
    onChange,
    onFocus,
    parse,
    resource,
    source,
    validate,
    choices = [],
    classes,
    hasText,
    meta,
    isRequired,
    optionValue,
    optionText,
    label,
    optionTextLabel,
    optionValueLabel,
    additionalColumns = [],
    sortFunc,
    singleSelect,
    disableSelectAll,
    disableCheckBoxes,
    translateChoice,
    translate,
    height,
    emptyMessage,
    additionalFilterProps,
    showAdditionalFilter,
    ...rest
  } = props;

  const {
    input: { onChange: finalFormOnChange, onBlur: finalFormOnBlur, value }
  } = useInput({
    format,
    onBlur,
    onChange,
    onFocus,
    parse,
    resource,
    source,
    validate,
    ...rest
  });
  const { error, touched } = meta;
  const [itemSearch, setItemSearch] = useState('');
  const [additionalItemSearch, setAdditionalItemSearch] = useState('');
  const [isShiftDown, setIsShiftDown] = useState(false);
  const [lastSelectedItem, setLastSelectedItem] = useState(null);
  const itemSelection = useMemo(() => value || [], [value]);

  /**
   * Set the itemSearch state to filter the right side list
   */
  const handleItemSearch = debounce(text => {
    setItemSearch(text);
  }, 500);

  const getAdditionalFilterChoices = useMemo(() => {
    if (!isNil(additionalFilterProps)) {
      const { attributeToFilter } = additionalFilterProps;
      return choices.reduce((acc, curr) => {
        if (curr[attributeToFilter]) {
          acc.push({
            id: curr[attributeToFilter],
            name: curr[attributeToFilter]
          });
        }
        return uniqBy(acc, 'id').sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
      }, []);
    }
  }, [additionalFilterProps, choices]);

  const handleKeyUp = useCallback(
    e => {
      if (e.key === 'Shift' && isShiftDown) {
        setIsShiftDown(false);
      }
    },
    [isShiftDown]
  );

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Shift' && !isShiftDown) {
        setIsShiftDown(true);
      }
    },
    [isShiftDown]
  );

  useEffect(() => {
    document.addEventListener('keyup', handleKeyUp, false);
    document.addEventListener('keydown', handleKeyDown, false);
    return () => {
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, handleKeyUp]);

  useEffect(() => {
    finalFormOnChange(value || []);
  }, [value, finalFormOnChange]);

  useEffect(() => {
    return () => {
      handleItemSearch.cancel();
    };
  }, [handleItemSearch]);

  // Sort choices if needed
  const sortedChoices = useMemo(
    () => (sortFunc ? choices.sort(sortFunc) : choices),
    [sortFunc, choices]
  );

  const filteredChoices = useMemo(() => {
    return sortedChoices.filter(choice => {
      const value =
        typeof optionValue === 'function'
          ? optionValue(choice)
          : get(choice, optionValue);
      const text =
        typeof optionText === 'function'
          ? optionText(choice)
          : get(choice, optionText);

      const additionalColumnValues = additionalColumns.map(column => {
        const addValue = get(choice, column.name);
        return addValue ? addValue.toString().toLowerCase() : '';
      });

      return (
        ((!isNil(value) &&
          value
            .toString()
            .toLowerCase()
            .includes(itemSearch.toLowerCase())) ||
          (!isNil(text) &&
            text.toLowerCase().includes(itemSearch.toLowerCase())) ||
          (additionalColumns.length > 0 &&
            additionalColumnValues.some(val =>
              val.includes(itemSearch.toLowerCase())
            ))) &&
        (additionalItemSearch === ''
          ? true
          : !!get(choice, additionalFilterProps.attributeToFilter)
              ?.toLowerCase()
              .includes(additionalItemSearch.toLowerCase()))
      );
    });
  }, [
    sortedChoices,
    optionValue,
    optionText,
    itemSearch,
    additionalColumns,
    additionalFilterProps,
    additionalItemSearch
  ]);

  const filteredSelectedChoices = useMemo(() => {
    return filteredChoices.filter(choice => {
      const value =
        typeof optionValue === 'function'
          ? optionValue(choice)
          : get(choice, optionValue);
      return itemSelection.includes(value);
    });
  }, [filteredChoices, optionValue, itemSelection]);

  /**
   * return the list of items selected with shift
   */
  const getShiftSelectedItems = useCallback(
    val => {
      const currentSelectedIndex = choices.findIndex(choice => {
        const value =
          typeof optionValue === 'function'
            ? optionValue(choice)
            : get(choice, optionValue);
        return value === val;
      });
      const lastSelectedIndex = choices.findIndex(choice => {
        const value =
          typeof optionValue === 'function'
            ? optionValue(choice)
            : get(choice, optionValue);
        return value === lastSelectedItem;
      });

      return choices
        .slice(
          Math.min(lastSelectedIndex, currentSelectedIndex),
          Math.max(lastSelectedIndex, currentSelectedIndex) + 1
        )
        .map(choice => {
          return typeof optionValue === 'function'
            ? optionValue(choice)
            : get(choice, optionValue);
        });
    },
    [choices, lastSelectedItem, optionValue]
  );

  const handleChange = useCallback(
    value => {
      finalFormOnChange(value);
      finalFormOnBlur(); // HACK: See https://github.com/final-form/react-final-form/issues/365#issuecomment-515045503
    },
    [finalFormOnChange, finalFormOnBlur]
  );

  /**
   * Render item label
   */
  const renderItemLabel = useCallback(
    choice => {
      if (React.isValidElement(optionText))
        return React.cloneElement(optionText, {
          record: choice
        });
      const choiceName =
        typeof optionText === 'function'
          ? optionText(choice)
          : get(choice, optionText);

      return translateChoice
        ? translate(choiceName, { _: choiceName })
        : choiceName;
    },
    [optionText, translate, translateChoice]
  );

  /**
   * Select list item => add to itemSelection list
   */
  const handleItemSelected = useCallback(
    value => {
      let array;

      if (singleSelect) {
        array = [...itemSelection];
        if (array.includes(value)) {
          array = [];
        } else {
          array = [value];
        }
      } else {
        if (isShiftDown) {
          const hasBeenSelected = !itemSelection.includes(value);
          const shiftSelectedItems = getShiftSelectedItems(value);
          // de-dupe the array using a Set
          array = [...new Set([...itemSelection, ...shiftSelectedItems])];

          if (!hasBeenSelected) {
            array = array.filter(item => !shiftSelectedItems.includes(item));
          }
        } else {
          array = [...itemSelection];
          if (array.includes(value)) {
            array.splice(array.indexOf(value), 1);
          } else {
            array.push(value);
          }
        }
      }

      setLastSelectedItem(value);
      handleChange(array);
    },
    [
      getShiftSelectedItems,
      handleChange,
      isShiftDown,
      itemSelection,
      singleSelect
    ]
  );

  /**
   * Select all list items => add to itemSelection list
   */
  const handleAllItemsSelected = useCallback(() => {
    const array = [...itemSelection];

    const alreadySelectedChoices = filteredChoices.filter(choice => {
      const value =
        typeof optionValue === 'function'
          ? optionValue(choice)
          : get(choice, optionValue);
      return itemSelection.includes(value);
    });

    if (alreadySelectedChoices.length < filteredChoices.length) {
      filteredChoices.forEach(choice => {
        const value =
          typeof optionValue === 'function'
            ? optionValue(choice)
            : get(choice, optionValue);

        const selectedChoice = alreadySelectedChoices.find(item => {
          const selectedValue =
            typeof optionValue === 'function'
              ? optionValue(item)
              : get(item, optionValue);
          return value === selectedValue;
        });

        if (!selectedChoice) {
          array.push(value);
        }
      });
    } else {
      alreadySelectedChoices.forEach(choice => {
        const value =
          typeof optionValue === 'function'
            ? optionValue(choice)
            : get(choice, optionValue);
        array.splice(array.indexOf(value), 1);
      });
    }

    handleChange(array);
  }, [handleChange, itemSelection, optionValue, filteredChoices]);

  const getItemValue = useCallback(
    item => {
      return typeof optionValue === 'function'
        ? optionValue(item)
        : get(item, optionValue);
    },
    [optionValue]
  );

  const getItemStatus = useCallback(
    item => {
      return typeof optionValue === 'function'
        ? optionValue(item)
        : get(item, 'status');
    },
    [optionValue]
  );

  const getItemText = useCallback(
    item => {
      return typeof optionText === 'function'
        ? optionText(item)
        : get(item, optionText);
    },
    [optionText]
  );

  const columns = useMemo(() => {
    const additionalColumnsContains1Column = additionalColumns.length === 1;
    const sizeFor2ColumnsOptionLabel =
      additionalColumns.length > 1 ? 0.4 : 0.55;
    const sizeFor2ColumnsOptionValue =
      additionalColumns.length > 1 ? 0.2 : 0.45;
    const sizeForOptionLabel = additionalColumnsContains1Column
      ? 0.55
      : sizeFor2ColumnsOptionLabel;
    const sizeForOptionValue = additionalColumnsContains1Column
      ? 0.3
      : sizeFor2ColumnsOptionValue;
    const columns = [
      {
        id: 'selected',
        source: 'isItemSelected',
        type: 'checkbox',
        width: 30,
        onClick: handleAllItemsSelected,
        renderer: params => {
          const { row_data, rowVal } = params;
          const cellStyle = {
            ...row_data.style,
            color: '#000000',
            borderTop: '1px solid #E0E0E0',
            fontSize: '14px',
            background: 'white',
            padding: 0
          };
          const value = getItemValue(rowVal);
          return (
            <div style={cellStyle} key={row_data.key}>
              <Checkbox
                id={value}
                data-testid="gick"
                classes={{ root: classes.root }}
                checked={itemSelection.includes(value)}
                onClick={() => handleItemSelected(value)}
                disabled={disableCheckBoxes}
              />
            </div>
          );
        }
      },
      {
        id: optionTextLabel,
        source: 'textColumn',
        type: 'text',
        widthPercentage: sizeForOptionLabel,
        renderer: params => {
          const { column, row_data, rowVal } = params;
          const cellStyle = {
            ...row_data.style,
            color: '#000000',
            fontSize: '14px',
            fontWeight: '400',
            textAlign: 'left',
            background: 'white',
            paddingTop: '4px',
            paddingLeft: '8px',
            borderTop: '1px solid #E0E0E0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          };
          return (
            <LightTooltip
              title={rowVal[column.source] || ''}
              key={row_data.key}
              style={row_data.style}
            >
              <span style={cellStyle} id={optionTextLabel}>
                {renderItemLabel(rowVal)}
              </span>
            </LightTooltip>
          );
        }
      },
      {
        id: optionValueLabel,
        source: 'valueColumn',
        type: 'text',
        widthPercentage: sizeForOptionValue,
        renderer: params => {
          const { column, row_data, rowVal } = params;
          const cellStyle = {
            ...row_data.style,
            color: '#000000',
            fontSize: '14px',
            fontWeight: '400',
            textAlign: 'left',
            background: 'white',
            paddingTop: '4px',
            paddingLeft: '8px',
            borderTop: '1px solid #E0E0E0',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          };
          return (
            <LightTooltip
              title={rowVal[column.source]}
              key={row_data.key}
              style={row_data.style}
            >
              <span style={cellStyle} id={optionValueLabel}>
                {rowVal[column.source]}
              </span>
            </LightTooltip>
          );
        }
      }
    ];
    if (additionalColumns) {
      additionalColumns.forEach(additionalColumn => {
        columns.push({
          id: additionalColumn.label,
          source: additionalColumn.name,
          additionalColumn: true,
          type: 'text',
          widthPercentage: additionalColumn.isStatusColumn ? 0.15 : 0.25,
          renderer: params => {
            const { column, row_data, rowVal } = params;
            const cellStyle = {
              ...row_data.style,
              color: '#000000',
              fontSize: '14px',
              fontWeight: '400',
              textAlign: 'left',
              background: 'white',
              paddingTop: '4px',
              paddingLeft: '3px',
              borderTop: '1px solid #E0E0E0',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            };
            return (
              <LightTooltip
                title={get(rowVal, column.source)}
                key={row_data.key}
                style={row_data.style}
                //don't show tooltip on hover if this is a status column, or if the column don't have content
                disableHoverListener={
                  additionalColumn.isStatusColumn || !get(rowVal, column.source)
                }
              >
                <span style={cellStyle}>
                  {additionalColumn.isStatusColumn && (
                    <StatusColorDisplay status={get(rowVal, column.source)} />
                  )}
                  {!additionalColumn.isStatusColumn &&
                    get(rowVal, column.source)}
                </span>
              </LightTooltip>
            );
          }
        });
      });
    }
    return columns;
  }, [
    classes.root,
    getItemValue,
    handleAllItemsSelected,
    handleItemSelected,
    itemSelection,
    optionTextLabel,
    optionValueLabel,
    renderItemLabel,
    additionalColumns,
    disableCheckBoxes
  ]);

  const calculateColumnWidth = useCallback(
    (index, width) => {
      return columns[index]['source'] === 'isItemSelected'
        ? 30
        : columns[index]['widthPercentage'] * (width - 48);
    },
    [columns]
  );

  //calculate column values and add to row data
  const dataRows = useMemo(() => {
    return filteredChoices.map(item => {
      return {
        ...item,
        textColumn: getItemText(item),
        valueColumn: getItemValue(item),
        statusColumn: getItemStatus(item)
      };
    });
  }, [filteredChoices, getItemText, getItemValue, getItemStatus]);

  //add column row
  const columnRow = useMemo(() => {
    return columns.reduce((acc, cur) => ({ ...acc, [cur.source]: cur.id }), {});
  }, [columns]);

  const gridData = useMemo(() => {
    return [columnRow, ...dataRows];
  }, [columnRow, dataRows]);

  const isAdditionalFilter =
    showAdditionalFilter && getAdditionalFilterChoices.length > 1;

  return (
    <Grid container spacing={1} justifyContent="center">
      <Grid item xs={12}>
        <Grid item xs={12}>
          <InputLabel htmlFor={source} shrink>
            <FieldTitle
              label={label}
              source={source}
              resource={resource}
              isRequired={isRequired}
            />
          </InputLabel>
        </Grid>
        <Grid container spacing={isAdditionalFilter ? 1 : 0}>
          <Grid item xs={isAdditionalFilter ? 6 : 12}>
            <TextField
              margin="dense"
              placeholder={`Search...`}
              onChange={e => handleItemSearch(e.target.value)}
            />
          </Grid>
          {isAdditionalFilter && (
            <Grid item xs={6} data-testid="custom-element">
              <Autocomplete
                options={getAdditionalFilterChoices}
                getOptionLabel={option => option.name}
                inputValue={additionalItemSearch}
                onInputChange={(_event, newInputValue) => {
                  setAdditionalItemSearch(newInputValue);
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Fund Family"
                    margin="dense"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        <Paper>
          {(sortedChoices.length > 0 || emptyMessage !== '') && (
            <VirtualizedGrid
              columns={columns}
              data={gridData}
              selectedRows={itemSelection.length}
              disableSelectAll={
                singleSelect || disableSelectAll || disableCheckBoxes
              }
              filteredRows={filteredChoices}
              filteredSelectedRows={filteredSelectedChoices}
              calculateColumnWidth={calculateColumnWidth}
              resizibleColumns={false}
              fixedColumns={0}
              width={'calc(100% - 2px)'}
              height={height ? height : '100%'}
              minHeight={300}
              rowHeight={30}
              itemSearch={itemSearch}
              emptyMessage={emptyMessage}
            />
          )}
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Grid container justifyContent="flex-end">
          <Typography variant="caption">
            {itemSelection.length} item{itemSelection.length > 1 && 's'}{' '}
            selected
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        {!!(touched && error) && <FormHelperText error>{error}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

GridInput.propTypes = {
  choices: PropTypes.arrayOf(PropTypes.object),
  classes: PropTypes.object,
  className: PropTypes.string,
  input: PropTypes.object,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  meta: PropTypes.object,
  options: PropTypes.object,
  optionText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.element
  ]).isRequired,
  optionValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    .isRequired,
  disableValue: PropTypes.string,
  resource: PropTypes.string,
  sortFunc: PropTypes.func,
  source: PropTypes.string,
  translate: PropTypes.func,
  translateChoice: PropTypes.bool,
  optionTextLabel: PropTypes.string,
  optionValueLabel: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disableSelectAll: PropTypes.bool,
  singleSelect: PropTypes.bool,
  disableCheckBoxes: PropTypes.bool,
  emptyMessage: PropTypes.string
};

GridInput.defaultProps = {
  classes: {},
  choices: [],
  options: {},
  emptyMessage: '',
  optionText: 'name',
  optionValue: 'id',
  translateChoice: true,
  disableValue: 'disabled',
  hasText: true,
  singleSelect: false,
  disableSelectAll: false
};

export default withStyles(styles)(GridInput);
